import React, { ReactElement } from 'react';
import { useCommon } from 'src/AppState/Common';

import { BonusCreationStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { BonusCreationState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreation.state';
import { BonusCreationViewManager } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreationViewManager';

import { BadgeStatus, BadgeStatusVariantType } from 'src/domains/common/components/badge/badge';
import { observer } from 'src/utils/mobx-react';
import {
    DraftBonusCreationButtons,
    NewBonusCreationButtons,
    PublishedBonusCreationButtons,
} from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreationHeaderButtons';

function formTitle(routerBackoffice: RouterBackoffice, bonusCreationStatus?: BonusCreationStatusType): string {
    if (typeof routerBackoffice.bonusCampaignCloneIdParam === 'string') {
        return 'Duplicated Bonus';
    } else if (typeof routerBackoffice.bonusCreationIdParam === 'string' && bonusCreationStatus !== undefined) {
        return `${bonusCreationStatus.charAt(0).toUpperCase() + bonusCreationStatus.slice(1)} Bonus`;
    }
    return 'New Bonus';
}

function formBadgeVariant(routerBackoffice: RouterBackoffice): BadgeStatusVariantType {
    return typeof routerBackoffice.bonusCreationIdParam === 'string' ? 'edit' : 'unsaved';
}

interface RewardBalanceHeaderPropsType {
    routerBackoffice: RouterBackoffice;
    bonusCreationId: string | undefined;
    bonusRewardState: BonusCreationViewManager;
}

export const BonusCreationHeader = observer(
    'BonusCreationHeader',
    ({ routerBackoffice, bonusCreationId, bonusRewardState }: RewardBalanceHeaderPropsType): ReactElement | null => {
        const common = useCommon();
        const bonusCreationState = BonusCreationState.get(common, bonusCreationId);

        const { handleCloseWithDiscard } = bonusRewardState;
        const bonusCreationStatus = bonusCreationState.bonusCreation?.status;

        return (
            <header className='flex items-center justify-between max-w-full'>
                <div className='min-w-0'>
                    <h1
                        className='truncate m-0 text-[1.5rem] font-semibold leading-[1.25] mb-2'
                        data-test='bonus-creation-name'
                    >
                        {bonusCreationState.generalDetailsState.bonusRewardDisplayName}
                    </h1>
                    <div className='flex items-center gap-3'>
                        <p
                            className='m-0'
                            data-test='bonusCreation-form-title'
                        >
                            {formTitle(routerBackoffice, bonusCreationStatus)}
                        </p>
                        <BadgeStatus
                            variant={formBadgeVariant(routerBackoffice)}
                            dataTest='bonusCreation-status'
                        />
                    </div>
                </div>
                <div className='flex flex-none gap-2'>
                    {routerBackoffice.bonusCreationParams?.type === 'NEW' ? (
                        <NewBonusCreationButtons
                            onClose={handleCloseWithDiscard}
                            onSaveAsDraft={() =>
                                bonusCreationState.createBonusCreation('draft', routerBackoffice.bonusCreationParams)
                            }
                            onPublish={() =>
                                bonusCreationState.createBonusCreation(
                                    'published',
                                    routerBackoffice.bonusCreationParams
                                )
                            }
                        />
                    ) : (
                        <>
                            {bonusCreationStatus === 'draft' && (
                                <DraftBonusCreationButtons
                                    onClose={handleCloseWithDiscard}
                                    onUpdate={bonusCreationState.updateBonusCreation}
                                    onPublish={() =>
                                        bonusCreationState.createBonusCreation(
                                            'published',
                                            routerBackoffice.bonusCreationParams
                                        )
                                    }
                                />
                            )}
                            {bonusCreationStatus === 'published' && (
                                <PublishedBonusCreationButtons
                                    onClose={handleCloseWithDiscard}
                                    onUnpublish={() => bonusCreationState.confirmUnpublishBonusCreation()}
                                />
                            )}
                        </>
                    )}
                </div>
            </header>
        );
    }
);
